import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";
import { apiErrorMessage, fetchAccess, saveAccessEmails, type AccessInfo } from "../services/expensesService";
import { clearExpensesAccess } from "../hooks/useExpensesAccess";
import { Chip, ErrorBox, Notice, Page } from "../components/ui";
import { ui } from "../theme";

// Quién puede ver Gastos y Presupuestos (#608). Los correos fijos vienen del
// código/servidor; los demás se agregan aquí.

export default function AccessSection() {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const c = ui(isDarkMode);
  const [access, setAccess] = useState<AccessInfo | null>(null);
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    fetchAccess().then(setAccess).catch((err) => setError(apiErrorMessage(err) || "load"));
  }, []);

  const save = async (emails: string[]) => {
    setSaved(false);
    try {
      setAccess(await saveAccessEmails(emails));
      clearExpensesAccess();
      setError(null);
      setSaved(true);
    } catch (err) {
      setError(apiErrorMessage(err) || "save");
    }
  };

  const savedEmails = access?.savedEmails || [];
  const add = () => {
    const e = email.trim().toLowerCase();
    if (!e) return;
    void save([...savedEmails, e]);
    setEmail("");
  };

  const errorText = error === "load" ? t("expenses.errors.load") : error === "save" ? t("expenses.errors.save") : error;

  return (
    <Page title={t("expenses.menu.access")} subtitle={t("expenses.access.subtitle")}>
      <ErrorBox message={errorText} />
      {saved && <Notice tone="ok">{t("expenses.access.saved")}</Notice>}
      <div className={c.card}>
        <h3 className={`mb-2 text-sm font-semibold ${c.text}`}>{t("expenses.access.fixed")}</h3>
        <p className={`mb-2 text-xs ${c.muted}`}>{t("expenses.access.fixedHint")}</p>
        <div className="flex flex-wrap gap-2">
          {(access?.fixedEmails || []).map((e) => (
            <Chip key={e} className="bg-teal-500/15 text-teal-700 dark:text-teal-300">
              <i className="ri-lock-line mr-1" />
              {e}
            </Chip>
          ))}
        </div>
      </div>
      {access?.accessLocked ? (
        // CC-CONSOLE (#1007): las Finanzas internas son solo de los dueños; la
        // lista no se amplía ni desde aquí ni por variable de entorno.
        <Notice tone="ok">{t("expenses.access.locked")}</Notice>
      ) : (
      <div className={c.card}>
        <h3 className={`mb-2 text-sm font-semibold ${c.text}`}>{t("expenses.access.added")}</h3>
        <div className="mb-3 flex gap-2">
          <input
            type="email"
            aria-label={t("expenses.access.email")}
            className={c.input}
            placeholder="nombre@codingcloud.online"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
          />
          <button type="button" className={c.primary} disabled={!email.includes("@")} onClick={add}>
            {t("expenses.access.add")}
          </button>
        </div>
        <ul className="space-y-1">
          {savedEmails.map((e) => (
            <li key={e} className={`flex items-center justify-between rounded-lg border px-3 py-2 text-sm ${c.border} ${c.text}`}>
              <span>
                {e}
                {e === access?.email && <span className={`ml-2 text-xs ${c.muted}`}>({t("expenses.access.you")})</span>}
              </span>
              <button type="button" aria-label={`remove-${e}`} className="text-red-500" onClick={() => save(savedEmails.filter((x) => x !== e))}>
                <i className="ri-delete-bin-line" />
              </button>
            </li>
          ))}
          {savedEmails.length === 0 && <li className={`text-xs ${c.muted}`}>{t("expenses.access.none")}</li>}
        </ul>
      </div>
      )}
    </Page>
  );
}
